import React, { memo } from "react";
import { IconCheck, IconChecks, IconClock, IconAlertCircle } from "@tabler/icons-react";
import MessageMedia from "./MessageMedia";
import { formatMessageTime } from "../../utils/formatters";
import "./MessageBubble.css";

/**
 * Extrai assinatura do operador do texto da mensagem
 * Formato esperado: *Nome* ou *Nome - Departamento*
 */
const extractOperatorSignature = (text, explicitSignature) => {
  // Se tem assinatura explícita (mensagem otimista), usa ela
  if (explicitSignature) {
    return {
      signature: explicitSignature.replace(/^\*|\*$/g, ""), // Remove asteriscos
      text: text,
    };
  }

  // Tenta extrair do texto (formato: *Nome*\n ou *Nome - Dept*\n)
  const signatureMatch = text?.match(/^\*([^*]+)\*\n(.*)$/s);
  if (signatureMatch) {
    return {
      signature: signatureMatch[1],
      text: signatureMatch[2],
    };
  }

  return { signature: null, text };
};

/**
 * Converte URLs no texto em links clicáveis
 */
const linkifyText = (text) => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="sc-bubble__link">
        {part}
      </a>
    ) : (
      part
    )
  );
};

/**
 * Bolha de mensagem individual
 */
const MessageBubble = memo(({ message, isFromMe, showTail = true, onReplyComment }) => {
  // Verificar se é áudio gravado pelo operador (mensagem otimista)
  const isLocalAudio = message.isAudio && message.audioUrl;

  // Verificar se tem mídia (da API ou local)
  const hasMedia = isLocalAudio ||
    (message.type && message.type !== "text" && message.type !== "chat") ||
    (message.media_type && message.media_type !== "text") ||
    message.has_media;

  // Compatibilidade: usar body ou text (WebSocket usa text, API usa body)
  const rawMessageText = message.body || message.text || "";

  // Extrair assinatura do operador (se houver)
  const { signature: operatorSignature, text: messageText } = extractOperatorSignature(
    rawMessageText,
    message.operatorSignature
  );

  // Status da mensagem (para mensagens enviadas)
  const getStatusIcon = () => {
    if (!isFromMe) return null;

    const status = message.ack || message.status;

    switch (status) {
      case 0:
      case "pending":
        return <IconClock size={14} className="sc-bubble__status sc-bubble__status--pending" />;
      case 1:
      case "sent":
        return <IconCheck size={14} className="sc-bubble__status sc-bubble__status--sent" />;
      case 2:
      case "delivered":
        return <IconChecks size={14} className="sc-bubble__status sc-bubble__status--delivered" />;
      case 3:
      case 4:
      case "read":
        return <IconChecks size={14} className="sc-bubble__status sc-bubble__status--read" />;
      case -1:
      case "error":
      case "failed":
        return <IconAlertCircle size={14} className="sc-bubble__status sc-bubble__status--error" />;
      default:
        return <IconCheck size={14} className="sc-bubble__status sc-bubble__status--sent" />;
    }
  };

  // Detectar se é mensagem do sistema
  const isSystemMessage = message.type === "system" || message.isSystemMessage;

  if (isSystemMessage) {
    return (
      <div className="sc-bubble sc-bubble--system">
        <span className="sc-bubble__system-text">{messageText}</span>
      </div>
    );
  }

  // Badge para mensagens que vieram de comentário do Instagram
  const isInstagramComment =
    message.channel === 'instagram' && message.message_kind === 'comment';

  return (
    <div className={`sc-bubble ${isFromMe ? "sc-bubble--sent" : "sc-bubble--received"} ${showTail ? "sc-bubble--with-tail" : ""}`}>
      <div className="sc-bubble__content">
        {isInstagramComment && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
            <span
              className="sc-bubble__channel-badge"
              style={{
                fontSize: 11,
                color: '#7c3aed',
                background: '#f3e8ff',
                padding: '2px 8px',
                borderRadius: 8,
                display: 'inline-block',
              }}
              title={message.media_post_id ? `Post ID: ${message.media_post_id}` : ''}
            >
              💬 via comentário no Instagram
            </span>
            {!isFromMe && onReplyComment && (
              <button
                type="button"
                onClick={() => onReplyComment(message)}
                style={{
                  fontSize: 11,
                  color: '#7c3aed',
                  background: 'transparent',
                  border: '1px solid #7c3aed',
                  padding: '2px 8px',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
                title="Responder a este comentário publicamente no post"
              >
                Responder comentário
              </button>
            )}
          </div>
        )}

        {/* Áudio local (gravado pelo operador) */}
        {isLocalAudio && (
          <div className="sc-bubble__audio">
            <audio
              controls
              preload="auto"
              style={{ maxWidth: "250px", minWidth: "200px" }}
            >
              <source src={message.audioUrl} type="audio/webm;codecs=opus" />
            </audio>
          </div>
        )}

        {/* Mídia da API (não áudio local) */}
        {hasMedia && !isLocalAudio && <MessageMedia message={message} isFromMe={isFromMe} />}

        {/* Caption para mídia ou texto da mensagem */}
        {hasMedia && message.caption && (
          <p className="sc-bubble__text sc-bubble__caption">
            {linkifyText(message.caption)}
          </p>
        )}

        {/* Assinatura do operador (se houver) */}
        {isFromMe && operatorSignature && (
          <p className="sc-bubble__operator-signature">
            {operatorSignature}
          </p>
        )}

        {/* Texto (se não tem mídia) */}
        {!hasMedia && messageText && (
          <p className="sc-bubble__text">
            {linkifyText(messageText)}
          </p>
        )}

        {/* Footer com hora e status */}
        <div className="sc-bubble__footer">
          <span className="sc-bubble__time">
            {formatMessageTime(message.timestamp || message.created_at)}
          </span>
          {getStatusIcon()}
        </div>
      </div>
    </div>
  );
});

MessageBubble.displayName = "MessageBubble";

export default MessageBubble;
